import { useCallback, useEffect, useRef, useState } from 'react';

// How long the splash stays up even when auth is instant: long enough for the
// whole artboard sequence to play (the sticky note lands at ~2.95 s and its
// cursor is gone by 3.4 s), and the same point at which the design starts
// its exit.
export const MIN_VISIBLE_DURATION = 3100;
// Must match the overlay's opacity/transform transition in SplashScreen.module.css.
export const EXIT_DURATION = 550;

/**
 * Drives SplashScreen's exit. Call `finishLoading()` once the page is ready:
 * it waits out whatever is left of the minimum hold, flips `exiting` so the
 * overlay fades, then calls `onDone` (the caller's "stop loading" setter)
 * once the fade has played. Unmounting cancels everything.
 */
export const useSplashExit = (onDone) => {
  const [exiting, setExiting] = useState(false);
  // Wall clock of the first render, i.e. when the splash actually appeared.
  const shownAtRef = useRef(Date.now());
  const holdTimeoutRef = useRef(null);
  const exitTimeoutRef = useRef(null);
  // Read through a ref so a caller passing a fresh arrow each render still
  // gets its latest one.
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => () => {
    clearTimeout(holdTimeoutRef.current);
    clearTimeout(exitTimeoutRef.current);
  }, []);

  const finishLoading = useCallback(() => {
    const remainingHold = Math.max(0, MIN_VISIBLE_DURATION - (Date.now() - shownAtRef.current));
    // Last call wins: drop whichever timer is pending, hold or exit.
    clearTimeout(holdTimeoutRef.current);
    clearTimeout(exitTimeoutRef.current);
    holdTimeoutRef.current = setTimeout(() => {
      setExiting(true);
      exitTimeoutRef.current = setTimeout(() => onDoneRef.current(), EXIT_DURATION);
    }, remainingHold);
  }, []);

  return { exiting, finishLoading };
};
